import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { useLocation, openLocationPicker } from "@/contexts/LocationContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Pagination } from "@/components/Pagination";
import ProductTile from "@/components/ProductTile";
import { BannerSlider } from "@/components/Sliders";
import { useSiteSlides } from "@/components/SiteSlides";
import { CARE_URL, COMMUNITY_URL } from "@/lib/sites";
import { Search, Repeat, Store, BadgePercent } from "lucide-react";

const PAGE_SIZE = 24;

const SORTS = [["popular", "Popular near you"], ["price_low", "Price: low to high"], ["price_high", "Price: high to low"], ["discount", "Biggest discount"], ["newest", "Newest"]];

/** Categories in tree order, each followed by everything under it, at any depth. */
function categoryTree(cats) {
  const kids = {};
  cats.forEach((c) => { (kids[c.parent_id || "root"] = kids[c.parent_id || "root"] || []).push(c); });
  const out = [];
  const seen = new Set();
  const walk = (key, depth) => (kids[key] || []).sort((a, b) => a.name.localeCompare(b.name)).forEach((c) => {
    if (seen.has(c.id)) return;
    seen.add(c.id);
    out.push({ c, depth });
    walk(c.id, depth + 1);
  });
  walk("root", 0);
  return out;
}

export default function Browse() {
  const [sp, setSp] = useSearchParams();
  const { locationParams, locationLabel, looking } = useLocation();
  // The filters live in the address (?category=…&pet_type=…), so the menus
  // and the back button all change what this page shows.
  const category = sp.get("category") || "all";
  const subOnly = sp.get("subscription") === "1";
  const offersOnly = sp.get("offers") === "1";
  const petType = sp.get("pet_type") || "all";
  const brand = sp.get("brand") || "all";
  const sort = sp.get("sort") || "popular";
  const q = sp.get("q") || "";
  const page = Math.max(1, Number(sp.get("page")) || 1);
  const setFilter = (key, value) => {
    const next = new URLSearchParams(sp);
    if (value && value !== "all") next.set(key, value); else next.delete(key);
    if (key !== "page") next.delete("page");
    setSp(next, { replace: key !== "page" });
  };
  const setCategory = (v) => setFilter("category", v);
  const setSubOnly = (on) => setFilter("subscription", on ? "1" : "");
  const setOffersOnly = (on) => setFilter("offers", on ? "1" : "");
  const setPetType = (v) => setFilter("pet_type", v);
  const setBrand = (v) => setFilter("brand", v);
  const setSort = (v) => setFilter("sort", v === "popular" ? "" : v);
  const setPage = (n) => { setFilter("page", n > 1 ? String(n) : ""); window.scrollTo({ top: 0, behavior: "smooth" }); };
  // Typing waits a moment before searching, so each letter isn't a request.
  const [typed, setTyped] = useState(q);
  useEffect(() => { setTyped(q); }, [q]);
  useEffect(() => {
    if (typed === q) return undefined;
    const t = setTimeout(() => setFilter("q", typed.trim() ? typed : ""), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed]);
  const [res, setRes] = useState({ items: [], total: 0, pages: 1, brands: [] });
  const [vendors, setVendors] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data || []), () => setCats([])); }, []);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = { ...locationParams, group: "family", page, per_page: PAGE_SIZE, sort };
    if (category !== "all") params.category = category;
    if (subOnly) params.subscription = true;
    if (offersOnly) params.on_offer = true;
    if (q.trim()) params.q = q.trim();
    if (petType !== "all") params.pet_type = petType;
    if (brand !== "all") params.brand = brand;
    Promise.all([
      api.get("/products", { params }).then(({ data }) => data, () => ({ items: [], total: 0, pages: 1, brands: [] })),
      // Shops only when the customer is searching.
      q.trim() ? api.get("/vendors", { params: { q: q.trim() } }).then(({ data }) => data || [], () => []) : Promise.resolve([]),
    ]).then(([p, v]) => { if (alive) { setRes(p); setVendors(v); setLoading(false); } });
    return () => { alive = false; };
  }, [locationParams, category, subOnly, offersOnly, q, petType, brand, sort, page]);

  const products = res.items;
  const brands = res.brands || [];
  const filtered = category !== "all" || subOnly || offersOnly || petType !== "all" || brand !== "all";
  const isSearching = q.trim().length > 0;
  const [slides] = useSiteSlides("shop", "shop", mediaUrl);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      {slides.length > 0 && !filtered && !isSearching && page === 1 && <BannerSlider slides={slides} className="mb-6" testid="shop-slides" />}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-6">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
            {isSearching ? `Results for "${q.trim()}"` : offersOnly ? "Offers near you" : petType !== "all" ? `${petType === "Small Pets" ? "Small pet" : petType.replace(/s$/, "")} products` : "All products"}
          </h1>
          <p className="text-sm text-stone-600 mt-1" data-testid="browse-summary">
            {loading ? "Loading…" : looking ? `${res.total} products from shops across Mumbai` : `${res.total} products from shops that deliver to ${locationLabel}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
          <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-full px-4 h-11 flex-1 min-w-0 sm:min-w-[16rem]">
            <Search className="h-4 w-4 text-stone-500 shrink-0" />
            <Input data-testid="browse-search-input" value={typed} onChange={e => setTyped(e.target.value)} placeholder="Search in the shop" aria-label="Search in the shop" className="border-0 focus-visible:ring-0 shadow-none px-0 min-w-0" />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="browse-sort" aria-label="Sort" className="w-48 h-11 rounded-full bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>{SORTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-6">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="browse-category-select" className="w-44 h-10 rounded-full bg-white"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {/* The category tree at any depth: a category also shows everything under it ("Pet Food" shows "Dry Food" etc.). */}
            {categoryTree(cats).map(({ c, depth }) => (
              <SelectItem key={c.id} value={c.name} className={depth ? "text-stone-600" : "font-semibold"} style={{ paddingLeft: `${0.5 + depth * 1.25}rem` }}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={petType} onValueChange={setPetType}>
          <SelectTrigger data-testid="browse-pet-type-select" className="w-36 h-10 rounded-full bg-white"><SelectValue placeholder="For" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All pets</SelectItem>
            <SelectItem value="Dogs">Dogs</SelectItem>
            <SelectItem value="Cats">Cats</SelectItem>
            <SelectItem value="Birds">Birds</SelectItem>
            <SelectItem value="Fish">Fish</SelectItem>
            <SelectItem value="Rabbits">Rabbits</SelectItem>
            <SelectItem value="Small Pets">Small pets</SelectItem>
          </SelectContent>
        </Select>

        {(brands.length > 1 || brand !== "all") && (
          <Select value={brand} onValueChange={setBrand}>
            <SelectTrigger data-testid="browse-brand-select" className="w-40 h-10 rounded-full bg-white"><SelectValue placeholder="Brand" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All brands</SelectItem>
              {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        <div className="flex items-center gap-2 rounded-full bg-white border border-stone-200 px-4 h-10">
          <Repeat className="h-4 w-4 text-teal" />
          <Label htmlFor="sub-only" className="text-sm font-medium cursor-pointer">Subscribable</Label>
          <Switch id="sub-only" data-testid="browse-sub-toggle" checked={subOnly} onCheckedChange={setSubOnly} />
        </div>

        <div className="flex items-center gap-2 rounded-full bg-white border border-stone-200 px-4 h-10">
          <BadgePercent className="h-4 w-4 text-terracotta" />
          <Label htmlFor="offers-only" className="text-sm font-medium cursor-pointer">On offer</Label>
          <Switch id="offers-only" data-testid="browse-offers-toggle" checked={offersOnly} onCheckedChange={setOffersOnly} />
        </div>

        {(filtered || q) && (
          <Button variant="ghost" onClick={() => setSp(new URLSearchParams(), { replace: true })} data-testid="browse-clear-btn" className="rounded-full">Clear filters</Button>
        )}
      </div>

      {/* Products section */}
      <section className="mb-10" data-testid="browse-products-section">
        {loading && !products.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
            {Array.from({ length: 10 }, (_, i) => <div key={i} className="aspect-[3/4] rounded-2xl bg-stone-100 animate-pulse" />)}
          </div>
        ) : products.length === 0 ? (
          isSearching ? <NoMatch q={q.trim()} /> : filtered ? (
            <Card className="p-6 text-sm text-stone-600 rounded-2xl border-stone-200 border-dashed">Nothing matches these filters near {locationLabel}.
              <button type="button" onClick={() => setSp(new URLSearchParams(), { replace: true })} className="ml-1 font-semibold text-teal hover:underline">Clear filters</button></Card>
          ) : <NoShopHere label={locationLabel} />
        ) : (
          <>
            <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 ${loading ? "opacity-60" : ""}`}>
              {products.map(p => <ProductTile key={p.id} p={p} testid={`browse-product-${p.id}`} />)}
            </div>
            <Pagination page={res.page || page} pageCount={res.pages || 1} onChange={setPage} total={res.total} pageSize={PAGE_SIZE} testidPrefix="browse-pg" />
          </>
        )}
      </section>

      {/* Shops section (only when searching) */}
      {isSearching && (
        <section className="mb-10" data-testid="browse-shops-section">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-stone-500 mb-3">Shops ({vendors.length})</div>
          {vendors.length === 0 ? (
            <Card className="p-6 text-sm text-stone-600 rounded-2xl border-stone-200 border-dashed">No shops matched "{q.trim()}".</Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {vendors.map(v => (
                <Link key={v.id} to={`/vendor/${v.id}`} data-testid={`browse-shop-${v.id}`}>
                  <Card className="p-4 rounded-2xl border-stone-200 hover:shadow-md hover:-translate-y-1 transition-all flex items-center gap-3 h-full">
                    <div className="h-12 w-12 rounded-2xl bg-marigold/15 border border-marigold/30 flex items-center justify-center">
                      <Store className="h-5 w-5 text-marigold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading text-base font-semibold truncate">{v.shop_name}</div>
                      <div className="text-xs text-stone-500 truncate">{v.city ? `${v.city} · ` : ""}Pincode {v.pincode}</div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

    </div>
  );
}

/** Nothing found for a search: close matches from care and community, and
 *  popular searches. The one search box looks across all of PawBazaar. */
function NoMatch({ q }) {
  const [services, setServices] = useState([]);
  const [pets, setPets] = useState([]);
  const [ngos, setNgos] = useState([]);
  useEffect(() => {
    const word = q.split(/\s+/).find((w) => w.length > 3) || q;
    api.get("/care/service-types", { params: { q: word, per_page: 3 } }).then(({ data }) => setServices(data.items || []), () => setServices([]));
    api.get("/community/pets", { params: { q } }).then(({ data }) => setPets((data || []).slice(0, 3)), () => setPets([]));
    api.get("/community/ngos", { params: { q } }).then(({ data }) => setNgos((data || []).slice(0, 3)), () => setNgos([]));
  }, [q]);
  return (
    <div className="space-y-5" data-testid="browse-no-match">
      <div>
        <h2 className="font-heading text-2xl font-semibold">No products match "{q}"</h2>
        <p className="text-sm text-stone-600 mt-1">Try a shorter word, or look at these:</p>
      </div>
      {services.length > 0 && (
        <div className="rounded-2xl bg-[#E3F4F2] p-4 sm:p-5 space-y-2" data-testid="no-match-services">
          <div className="text-xs font-bold uppercase tracking-widest text-[#0F6E68]">Looking for a service?</div>
          {services.map((s) => (
            <a key={s.key} href={`${CARE_URL}/service/${s.key}`} className="flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 hover:shadow-sm">
              <span className="min-w-0"><b className="block">{s.name}</b><span className="text-sm text-stone-600">{s.providers} near you · from ₹{s.from_price}</span></span>
              <span className="shrink-0 rounded-full bg-[#0F6E68] text-white px-4 py-1.5 text-sm font-semibold">Book</span>
            </a>
          ))}
        </div>
      )}
      {(pets.length > 0 || ngos.length > 0) && (
        <div className="rounded-2xl bg-[#EAF1DF] p-4 sm:p-5 space-y-2" data-testid="no-match-community">
          <div className="text-xs font-bold uppercase tracking-widest text-[#3F6B2A]">On Community</div>
          {pets.map((p) => <a key={p.id} href={`${COMMUNITY_URL}/adopt/${p.id}`} className="block rounded-xl bg-white px-4 py-3 hover:shadow-sm"><b>{p.name}</b> <span className="text-sm text-stone-600">· up for adoption with {p.ngo?.name}</span></a>)}
          {ngos.map((n) => <a key={n.id} href={`${COMMUNITY_URL}/ngos/${n.id}`} className="block rounded-xl bg-white px-4 py-3 hover:shadow-sm"><b>{n.name}</b> <span className="text-sm text-stone-600">· verified NGO</span></a>)}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold mr-1">Popular:</span>
        {["Dog food", "Cat litter", "Treats", "Toys", "Shampoo"].map((w) => <Link key={w} to={`/shop?q=${encodeURIComponent(w)}`} className="rounded-full border border-stone-300 bg-white px-3 py-1 hover:border-stoneWarm">{w}</Link>)}
        <a href={`${CARE_URL}/services`} className="rounded-full border border-stone-300 bg-white px-3 py-1 hover:border-stoneWarm">Grooming</a>
        <a href={`${CARE_URL}/services?group=vet`} className="rounded-full border border-stone-300 bg-white px-3 py-1 hover:border-stoneWarm">Vet visit</a>
      </div>
    </div>
  );
}

/** No shop delivers to the chosen place: say so, and point to what is here. */
function NoShopHere({ label }) {
  return (
    <section className="rounded-3xl border border-dashed border-stone-300 bg-white p-5 sm:p-7 grid gap-5 md:grid-cols-[1.2fr_1fr]" data-testid="browse-no-shop">
      <div className="space-y-2">
        <h2 className="font-heading text-2xl font-semibold">No shop delivers to {label} yet</h2>
        <p className="text-stone-600">We are adding shops every week. Meanwhile you can:</p>
        <button type="button" onClick={() => openLocationPicker()} className="rounded-full bg-[#0F6E68] text-white px-5 py-2.5 font-semibold">Try another place</button>
      </div>
      <div className="space-y-3">
        <a href={`${CARE_URL}/services`} className="block rounded-2xl bg-[#E3F4F2] p-4"><b className="block">Book care near you</b><span className="text-sm text-stone-700">Groomers, vets and trainers</span></a>
        <a href={`${COMMUNITY_URL}/adopt`} className="block rounded-2xl bg-[#EAF1DF] p-4"><b className="block">Pets waiting for a home</b><span className="text-sm text-stone-700">With verified NGOs</span></a>
      </div>
    </section>
  );
}
